import React from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';

const LoanReport = ({ data, period }) => {
  const performance = data.performance || data;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold">Loan Performance Report</h2>
        {period && (
          <p className="text-gray-600">
            Period: {formatDate(period.from_date)} - {formatDate(period.to_date)}
          </p>
        )}
        <p className="text-gray-600">Generated on {formatDate(new Date())}</p>
      </div>

      {/* Loan Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
          <div className="text-sm opacity-90">Total Disbursed</div>
          <div className="text-2xl font-bold">{formatCurrency(performance.total_disbursed || 0)}</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
          <div className="text-sm opacity-90">Total Recovered</div>
          <div className="text-2xl font-bold">{formatCurrency(performance.total_recovered || 0)}</div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
          <div className="text-sm opacity-90">Outstanding</div>
          <div className="text-2xl font-bold">{formatCurrency((performance.total_disbursed || 0) - (performance.total_recovered || 0))}</div>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-4 text-white">
          <div className="text-sm opacity-90">Defaulted Amount</div>
          <div className="text-2xl font-bold">{formatCurrency(performance.defaulted_amount || 0)}</div>
        </div>
      </div>

      {/* Recovery Rate */}
      <div className="border rounded-lg p-4 mb-6">
        <h3 className="font-semibold mb-3">Recovery Performance</h3>
        <div className="mb-2">
          <div className="flex justify-between text-sm mb-1">
            <span>Recovery Rate</span>
            <span>{Math.round(((performance.total_recovered || 0) / (performance.total_disbursed || 1)) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-600 rounded-full h-3"
              style={{ width: `${((performance.total_recovered || 0) / (performance.total_disbursed || 1)) * 100}%` }}
            ></div>
          </div>
        </div>
        <div className="flex justify-between text-sm">
          <span>Target: 95%</span>
          <span className="text-green-600">Current: {Math.round(((performance.total_recovered || 0) / (performance.total_disbursed || 1)) * 100)}%</span>
        </div>
      </div>

      {/* Loan Portfolio by Type */}
      <div className="border rounded-lg mb-6 overflow-hidden">
        <h3 className="font-semibold p-4 border-b">Loan Portfolio by Type</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Loans</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disbursed Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recovered</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recovery Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Interest</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(performance.loan_portfolio || performance).map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">
                    {item.loan_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.total_loans}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.total_disbursed)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.total_recovered)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-green-600 rounded-full h-2"
                          style={{ width: `${Math.round((item.total_recovered / item.total_disbursed) * 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm">{Math.round((item.total_recovered / item.total_disbursed) * 100)}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.avg_interest_rate}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Due Loans Analysis */}
      {performance.due_loans && performance.due_loans.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <h3 className="font-semibold p-4 border-b bg-red-50">Due Loans Analysis</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Overdue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {performance.due_loans.map((loan, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {loan.loan_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {loan.member_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {formatCurrency(loan.due_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {loan.days_overdue} days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                        Overdue
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-right font-bold">Total Due:</td>
                  <td className="px-6 py-4 font-bold text-red-600">
                    {formatCurrency(performance.due_loans.reduce((sum, l) => sum + l.due_amount, 0))}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Loan Performance Metrics</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Average Loan Size</span>
              <span className="font-semibold">{formatCurrency((performance.total_disbursed || 0) / (performance.total_loans || 1))}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Average Interest Rate</span>
              <span className="font-semibold">{performance.avg_interest_rate || 0}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Average Duration</span>
              <span className="font-semibold">{Math.round(performance.avg_duration || 0)} months</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Active Loans</span>
              <span className="font-semibold">{performance.active_loans || 0}</span>
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Risk Assessment</h3>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Portfolio at Risk (PAR) > 30 days</span>
                <span>{Math.round((performance.par_30 || 0) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 rounded-full h-2"
                  style={{ width: `${(performance.par_30 || 0) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Default Rate</span>
                <span>{Math.round(((performance.defaulted_amount || 0) / (performance.total_disbursed || 1)) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-orange-600 rounded-full h-2"
                  style={{ width: `${((performance.defaulted_amount || 0) / (performance.total_disbursed || 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanReport;